import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider 
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  addDoc, 
  updateDoc, 
  serverTimestamp,
  orderBy,
  limit,
  deleteDoc
} from 'firebase/firestore';

// Firebase configuration from the real app
const firebaseConfig = {
  apiKey: "AIzaSyCzm-XQZSx8CWzQ8a8d_FUHJyLxGeTntYw",
  authDomain: "draftsimulatorapp.firebaseapp.com",
  projectId: "draftsimulatorapp",
  storageBucket: "draftsimulatorapp.appspot.com",
  messagingSenderId: "1043835478071",
  appId: "1:1043835478071:web:be5bee89f19496a2205fb2",
  measurementId: "G-506N49ENV7"
};

// Demo mode flag - set to false to use real Firebase
export const DEMO_MODE = false;

// Storage for demo data
export const demoStorage = {
  users: {},
  drafts: {}
};

// Initialize Firebase only if not in demo mode
let app, auth, db, googleProvider;

if (!DEMO_MODE) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  googleProvider = new GoogleAuthProvider();
} else {
  console.log('🧪 Running in DEMO MODE - no Firebase connection required');
  // Still create the GoogleAuthProvider for consistency
  googleProvider = new GoogleAuthProvider();
}

// Export auth objects for use elsewhere
export { auth, db, googleProvider };

class FirebaseService {
  constructor() {
    this.demoMode = DEMO_MODE;
    this.demoStorage = demoStorage;
  }

  // Authentication methods
  async signIn(email, password) {
    if (this.demoMode) {
      console.log('📝 Demo login with:', email);
      if (this.demoStorage.users[email] && this.demoStorage.users[email].password === password) {
        return { user: this.demoStorage.users[email] };
      } else {
        throw new Error('Invalid email or password');
      }
    } else {
      // First sign in with email and password
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Then check if the email is allowed
      const allowedEmailRef = doc(db, 'allowedEmails', email);
      const docSnap = await getDoc(allowedEmailRef);
      
      if (!docSnap.exists()) {
        // Check if the user is an admin
        const isAdmin = await this.isUserAdmin(result.user.uid);
        if (!isAdmin) {
          // If not allowed and not admin, sign out and throw error
          await signOut(auth);
          throw new Error('Access denied. Your email is not authorized to use this application.');
        }
      }
      
      return result;
    }
  }

  async signUp(email, password, displayName) {
    if (this.demoMode) {
      console.log('📝 Demo signup with:', email);
      if (this.demoStorage.users[email]) {
        throw new Error('User already exists');
      }
      
      // Create demo user
      const newUser = {
        uid: email,
        email,
        displayName,
        password
      };
      
      this.demoStorage.users[email] = newUser;
      this.demoStorage.drafts[email] = {};
      
      return { user: newUser };
    } else {
      // First check if the email is in the allowed list
      const allowedEmailRef = doc(db, 'allowedEmails', email);
      const docSnap = await getDoc(allowedEmailRef);
      
      if (!docSnap.exists()) {
        throw new Error('Registration is restricted. Your email is not authorized to register for this application.');
      }
      
      // If allowed, proceed with registration
      const result = await createUserWithEmailAndPassword(auth, email, password);
      // Save additional user info
      await setDoc(doc(db, 'users', result.user.uid), {
        displayName,
        email,
        createdAt: new Date().toISOString()
      });
      return result;
    }
  }

  async signOut() {
    if (this.demoMode) {
      // In demo mode, don't actually sign out
      console.log('📝 Demo sign out');
      window.location.href = '/';
      return Promise.resolve();
    }
    await signOut(auth);
    // Redirect to home page
    window.location.href = '/';
    return Promise.resolve();
  }

  // Google sign in
  async signInWithGoogle() {
    if (this.demoMode) {
      console.log('📝 Demo Google login');
      return { 
        user: {
          uid: 'demo-user-123',
          email: 'demo@example.com',
          displayName: 'Demo User'
        } 
      };
    } else {
      try {
        const result = await signInWithPopup(auth, googleProvider);
        const user = result.user;
        
        // Check if the user's email is allowed
        const allowedEmailRef = doc(db, 'allowedEmails', user.email);
        const docSnap = await getDoc(allowedEmailRef);
        
        if (!docSnap.exists()) {
          // Check if the user is an admin
          const isAdmin = await this.isUserAdmin(user.uid);
          if (!isAdmin) {
            // If not allowed and not admin, sign out and throw error
            await signOut(auth);
            throw new Error('Access denied. Your email is not authorized to use this application.');
          }
        }
        
        // Check if this is a new user
        const isNewUser = result._tokenResponse?.isNewUser;
        if (isNewUser) {
          // Save additional user info for new users
          await setDoc(doc(db, 'users', result.user.uid), {
            displayName: result.user.displayName,
            email: result.user.email,
            createdAt: new Date().toISOString()
          });
        }
        
        return result;
      } catch (error) {
        console.error("Error signing in with Google:", error);
        throw error;
      }
    }
  }

  // Current user
  getCurrentUser() {
    if (this.demoMode) {
      // In demo mode, always return a fake user
      return {
        uid: 'demo-user-123',
        email: 'demo@example.com',
        displayName: 'Demo User'
      };
    }
    return auth.currentUser;
  }

  // Check if the current user's email is in the allowed list
  async checkCurrentUserAccess() {
    if (this.demoMode) {
      return true; // In demo mode, always grant access
    }
    
    try {
      const user = this.getCurrentUser();
      if (!user) return false;
      
      // Check if the user's email is in the allowedEmails collection
      const allowedEmailRef = doc(db, 'allowedEmails', user.email);
      const docSnap = await getDoc(allowedEmailRef);
      
      if (docSnap.exists()) {
        console.log('User email is in allowed list:', user.email);
        return true;
      }
      
      // If not found, check if the user is an admin (admins always have access)
      const isAdmin = await this.isUserAdmin(user.uid);
      if (isAdmin) {
        console.log('User is an admin, granting access:', user.email);
        return true;
      }
      
      console.log('User email not in allowed list:', user.email);
      return false;
    } catch (error) {
      console.error('Error checking user access:', error);
      return false;
    }
  }
  
  // Check if a user is an admin
  async isUserAdmin(userId) {
    if (this.demoMode) {
      return userId === 'demo-user-123'; // Only the demo user is admin in demo mode
    }
    
    try {
      const userDocRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userDocRef);
      
      if (docSnap.exists()) {
        // Check if the user has an admin field set to true
        return docSnap.data().isAdmin === true;
      }
      return false;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  // Add onAuthStateChanged method
  onAuthStateChanged(callback) {
    if (this.demoMode) {
      // In demo mode, immediately call callback with demo user and return a no-op unsubscribe function
      callback({
        uid: 'demo-user-123',
        email: 'demo@example.com',
        displayName: 'Demo User'
      });
      return () => {};
    }
    
    // Use Firebase's onAuthStateChanged
    return onAuthStateChanged(auth, callback);
  }

  // Draft operations
  async saveDraft(draftData) {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error("User must be logged in to save a draft");
      }

      // Make sure userId is set
      draftData.userId = user.uid;
      draftData.userEmail = user.email;
      
      // Update timestamp
      draftData.updatedAt = new Date().toISOString();
      if (!draftData.createdAt) {
        draftData.createdAt = draftData.updatedAt;
      }
      
      // Set default visibility to private if not specified
      if (draftData.isPublic === undefined) {
        draftData.isPublic = false;
      }
      
      // Convert drafts to the correct format
      // This mirrors the _convertDraftsToMap method in the Flutter app
      if (draftData.radiantDrafts) {
        draftData.radiantDrafts = this._convertDraftsToMap(draftData.radiantDrafts, true);
      }
      
      if (draftData.direDrafts) {
        draftData.direDrafts = this._convertDraftsToMap(draftData.direDrafts, false);
      }
      
      // Extract core hero data for display in cards
      if (draftData.radiantHeroes && Array.isArray(draftData.radiantHeroes)) {
        // Store the picks and bans separately for display in cards
        draftData.radiantPicks = draftData.radiantHeroes
          .filter(hero => hero && !hero.isBanned)
          .map(hero => hero.hero);
          
        draftData.radiantBans = draftData.radiantHeroes
          .filter(hero => hero && hero.isBanned)
          .map(hero => hero.hero);
      }
      
      if (draftData.direHeroes && Array.isArray(draftData.direHeroes)) {
        // Store the picks and bans separately for display in cards
        draftData.direPicks = draftData.direHeroes
          .filter(hero => hero && !hero.isBanned)
          .map(hero => hero.hero);
          
        draftData.direBans = draftData.direHeroes
          .filter(hero => hero && hero.isBanned)
          .map(hero => hero.hero);
      }

      if (this.demoMode) {
        // Demo mode save draft
        // Generate an ID if it doesn't exist
        if (!draftData.id) {
          draftData.id = 'draft_' + Date.now();
        }
        
        // Initialize drafts collection if it doesn't exist
        if (!demoStorage.drafts[user.uid]) {
          demoStorage.drafts[user.uid] = {};
        }
        
        // Save to demo storage - only visible to the owner unless explicitly marked public
        demoStorage.drafts[user.uid][draftData.id] = draftData;
        console.log('Demo mode: Saved draft', draftData.id, draftData.isPublic ? '(public)' : '(private)');
        return draftData.id;
      } else {
        // Real Firebase implementation
        // Primary storage is in user's collection for privacy control
        let draftId = draftData.id;
        
        if (draftId) {
          // Update existing draft in user's collection first (primary storage)
          const userDraftRef = doc(db, 'users', user.uid, 'drafts', draftId);
          await setDoc(userDraftRef, draftData, { merge: true });
          
          // If public, also update in main collection for discoverability
          if (draftData.isPublic) {
            const draftDocRef = doc(db, 'drafts', draftId);
            await setDoc(draftDocRef, draftData, { merge: true });
            console.log('Firebase: Updated draft in both locations (public)', draftId);
          } else {
            // If private, remove from public collection if it exists there
            try {
              const draftDocRef = doc(db, 'drafts', draftId);
              const draftSnap = await getDoc(draftDocRef);
              
              if (draftSnap.exists() && draftSnap.data().userId === user.uid) {
                await deleteDoc(draftDocRef);
                console.log('Firebase: Made draft private, removed from public collection', draftId);
              }
            } catch (error) {
              console.error('Error checking/removing from public collection:', error);
            }
            
            console.log('Firebase: Updated draft in user collection only (private)', draftId);
          }
        } else {
          // Create new draft with generated ID
          // Always save in user's collection first
          const userDraftsCollection = collection(db, 'users', user.uid, 'drafts');
          const newUserDocRef = await addDoc(userDraftsCollection, draftData);
          draftId = newUserDocRef.id;
          
          // Update the draft with its ID
          draftData.id = draftId;
          await updateDoc(newUserDocRef, { id: draftId });
          
          // If public, also save in main collection for discoverability
          if (draftData.isPublic) {
            const draftDocRef = doc(db, 'drafts', draftId);
            await setDoc(draftDocRef, draftData);
            console.log('Firebase: Created new draft in both locations (public)', draftId);
          } else {
            console.log('Firebase: Created new draft in user collection only (private)', draftId);
          }
        }
        
        return draftId;
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      throw error;
    }
  }

  // Helper method to convert drafts to the format expected by the app
  // Mirrors the _convertDraftsToMap method in the Flutter app
  _convertDraftsToMap(drafts, isRadiant) {
    const draftMap = {};
    
    // Get sequence maps (same as in the Flutter app)
    const radiantSequenceMap = {
      0: '1', 1: '4', 2: '7', 3: '8', 4: '10', 5: '11',
      6: '14', 7: '15', 8: '18', 9: '19', 10: '22', 11: '23'
    };
    
    const direSequenceMap = {
      0: '2', 1: '3', 2: '5', 3: '6', 4: '9', 5: '12',
      6: '13', 7: '16', 8: '17', 9: '20', 10: '21', 11: '24'
    };
    
    const sequenceMap = isRadiant ? radiantSequenceMap : direSequenceMap;
    
    for (let i = 0; i < drafts.length; i++) {
      const scenarioMap = {};
      
      if (Array.isArray(drafts[i])) {
        // Process heroes - use the appropriate offset depending on whether it's radiant or dire
        const startIndex = isRadiant ? 0 : 0;
        const endIndex = isRadiant ? 12 : 24;
        
        for (let j = startIndex; j < endIndex; j++) {
          if (j < drafts[i].length && drafts[i][j]) {
            const heroData = drafts[i][j];
            if (heroData) {
              // Store by index
              scenarioMap[j.toString()] = heroData;
              
              // Also store by sequence number
              const idx = isRadiant ? j : j - 12;
              const seq = sequenceMap[idx];
              if (seq) {
                scenarioMap[seq] = heroData;
              }
            }
          }
        }
      }
      
      draftMap[i.toString()] = scenarioMap;
    }
    
    return draftMap;
  }

  // Format draft data for consumption by the app
  _formatDraftData(draftDoc) {
    const data = draftDoc.data ? draftDoc.data() : draftDoc;
    const id = draftDoc.id || data.id;
    
    return {
      id,
      title: data.title || '',
      tournament: data.tournament || '',
      date: data.date || '',
      updatedAt: data.updatedAt || new Date().toISOString(),
      createdAt: data.createdAt || data.updatedAt || new Date().toISOString(),
      userId: data.userId || '',
      radiantHeroes: data.radiantHeroes || [],
      direHeroes: data.direHeroes || [],
      radiantDrafts: data.radiantDrafts || {},
      direDrafts: data.direDrafts || {},
      radiantPicks: data.radiantPicks || [],
      direPicks: data.direPicks || [],
      radiantBans: data.radiantBans || [],
      direBans: data.direBans || [],
      isPublic: data.isPublic || false
    };
  }

  async getDraft(draftId) {
    try {
      const user = this.getCurrentUser();
      
      if (this.demoMode) {
        // Demo mode get draft
        if (!user) {
          console.log('Demo mode: User must be logged in to access drafts');
          return null;
        }
        
        // First check if this is the user's own draft
        const userId = user.uid;
        if (demoStorage.drafts[userId] && demoStorage.drafts[userId][draftId]) {
          console.log('Demo mode: Found draft for user', userId, draftId);
          return this._formatDraftData(demoStorage.drafts[userId][draftId]);
        }
        
        // If not found in user's drafts, check if it's a public draft from other users
        for (const otherUserId in demoStorage.drafts) {
          if (otherUserId !== userId && 
              demoStorage.drafts[otherUserId][draftId] && 
              demoStorage.drafts[otherUserId][draftId].isPublic) {
            console.log('Demo mode: Found public draft from another user', draftId);
            return this._formatDraftData(demoStorage.drafts[otherUserId][draftId]);
          }
        }
        
        console.log('Demo mode: Draft not found or not accessible', draftId);
        return null;
      } else {
        // Real Firebase implementation - check both potential locations
        if (!user) {
          console.log('Firebase: User must be logged in to access drafts');
          return null;
        }
        
        // First, check if it's the user's own draft
        // Try user-specific path first (preferred)
          const userDraftRef = doc(db, "users", user.uid, "drafts", draftId);
          const userDraftSnap = await getDoc(userDraftRef);
          
          if (userDraftSnap.exists()) {
            console.log('Firebase: Found draft at /users/{uid}/drafts path', draftId);
            return this._formatDraftData({
              id: userDraftSnap.id,
              data: () => userDraftSnap.data()
          });
        }
        
        // Try the direct /drafts/{draftId} path and check ownership
        const draftRef = doc(db, "drafts", draftId);
        const draftSnap = await getDoc(draftRef);
        
        if (draftSnap.exists()) {
          const draftData = draftSnap.data();
          
          // Check if this is the user's own draft
          if (draftData.userId === user.uid) {
            console.log('Firebase: Found user draft at /drafts path', draftId);
            return this._formatDraftData({
              id: draftSnap.id,
              data: () => draftData
            });
          }
          
          // Check if it's a public draft
          if (draftData.isPublic === true) {
            console.log('Firebase: Found public draft from another user', draftId);
            return this._formatDraftData({
              id: draftSnap.id,
              data: () => draftData
            });
          }
        }
        
        // Check public drafts by querying
        console.log('Firebase: Draft not found, searching in public drafts', draftId);
        const draftsRef = collection(db, "drafts");
        const q = query(
          draftsRef,
          where("isPublic", "==", true),
          where("id", "==", draftId)
        );
        
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const publicDraft = querySnapshot.docs[0];
          console.log('Firebase: Found public draft', draftId);
          return this._formatDraftData({
            id: publicDraft.id,
            data: () => publicDraft.data()
          });
        }
        
        console.log('Firebase: Draft not found or not accessible', draftId);
        return null;
      }
    } catch (error) {
      console.error("Error getting draft:", error);
      throw error;
    }
  }

  async getUserDrafts(userId) {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      throw new Error("User must be logged in to get drafts");
    }
    
    // Security check - users can only see their own drafts or public drafts
    if (userId !== currentUser.uid) {
      console.log('Attempted to access another user\'s drafts. Redirecting to public drafts.');
      return this.getPublicDrafts();
    }

    if (this.demoMode) {
      console.log('📝 Getting user drafts in demo mode for:', userId);
      const userDrafts = this.demoStorage.drafts[userId] || {};
      return Object.values(userDrafts)
        .map(draft => this._formatDraftData(draft))
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else {
      // Real Firebase implementation - use user collection for privacy
      const userDraftsRef = collection(db, "users", userId, "drafts");
      const userDraftsSnapshot = await getDocs(userDraftsRef);
      
      const drafts = userDraftsSnapshot.docs.map(doc => 
        this._formatDraftData({
          id: doc.id, 
          data: () => doc.data()
        })
      );
      
      return drafts.sort((a, b) => 
        new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    }
  }

  async getPublicDrafts() {
    const user = this.getCurrentUser();
    if (!user) {
      throw new Error("User must be logged in to view public drafts");
    }

    if (this.demoMode) {
      console.log('📝 Getting public drafts in demo mode');
      const publicDrafts = [];
      
      // Collect all public drafts from all users
      for (const userId in this.demoStorage.drafts) {
        // Skip current user's drafts - they'll see those in their getDrafts() call
        if (userId === user.uid) continue;
        
        const userDrafts = this.demoStorage.drafts[userId];
        for (const draftId in userDrafts) {
          // Only include drafts explicitly marked as public
          if (userDrafts[draftId].isPublic === true) {
            publicDrafts.push(this._formatDraftData(userDrafts[draftId]));
          }
        }
      }
      
      return publicDrafts.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else {
      // Real Firebase implementation - only get drafts from the public collection
      // The public collection only contains drafts explicitly marked as public
      const draftsRef = collection(db, 'drafts');
      const q = query(
        draftsRef,
        where('isPublic', '==', true),
        // Don't show the user's own drafts here, they'll see those in getDrafts()
        where('userId', '!=', user.uid),
        orderBy('userId'), // Required for inequality filter above
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => 
        this._formatDraftData({
          id: doc.id,
          data: () => doc.data()
        })
      );
    }
  }

  async deleteDraft(draftId) {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error("User must be logged in to delete a draft");
      }

      if (this.demoMode) {
        // Demo mode delete draft
        const userId = user.uid;
        
        if (!demoStorage.drafts[userId] || !demoStorage.drafts[userId][draftId]) {
          throw new Error("Draft not found or you don't have permission to delete it");
        }
        
        delete demoStorage.drafts[userId][draftId];
        console.log('Demo mode: Deleted draft', draftId);
        return true;
      } else {
        // Real Firebase delete draft - first check user's collection (primary storage)
        const userDraftRef = doc(db, "users", user.uid, "drafts", draftId);
        const userDraftSnap = await getDoc(userDraftRef);
        
        if (userDraftSnap.exists()) {
          // Delete from user's collection
          await deleteDoc(userDraftRef);
          console.log('Firebase: Deleted draft from user collection', draftId);
          
          // Also try to delete from main collection if it exists there
          try {
            const publicDocRef = doc(db, "drafts", draftId);
            const publicDocSnap = await getDoc(publicDocRef);
            
            if (publicDocSnap.exists() && publicDocSnap.data().userId === user.uid) {
              await deleteDoc(publicDocRef);
              console.log('Firebase: Also deleted draft from public collection', draftId);
            }
          } catch (error) {
            console.error("Error checking/deleting from public collection:", error);
          }
          
          return true;
        }
        
        // If not in user's collection, check main collection
        const docRef = doc(db, "drafts", draftId);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          throw new Error("Draft not found");
        }
        
        const draftData = docSnap.data();
        
        if (draftData.userId !== user.uid) {
          throw new Error("You don't have permission to delete this draft");
        }
        
        // Delete the document from main collection
        await deleteDoc(docRef);
        console.log('Firebase: Deleted draft from public collection only', draftId);
        return true;
      }
    } catch (error) {
      console.error("Error deleting draft:", error);
      throw error;
    }
  }

  async getDrafts() {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error("User must be logged in to get drafts");
      }

      if (this.demoMode) {
        // Demo mode get drafts
        const userId = user.uid;
        
        // Only get drafts that belong to this user
        const userDrafts = demoStorage.drafts[userId] || {};
        
        // Convert object to array with IDs properly set
        const draftsArray = Object.entries(userDrafts).map(([id, draft]) => 
          this._formatDraftData({...draft, id})
        );
        
        console.log('Demo mode: Retrieved drafts for user', userId, draftsArray.length);
        return draftsArray.sort((a, b) => 
          new Date(b.updatedAt) - new Date(a.updatedAt)
        );
      } else {
        // Real Firebase get drafts - ONLY get drafts belonging to current user
        const drafts = [];
        const draftIds = new Set(); // To track unique drafts
        
        try {
          // First, get drafts from user-specific collection (preferred storage)
          const userDraftsRef = collection(db, "users", user.uid, "drafts");
          const userDraftsSnapshot = await getDocs(userDraftsRef);
          
          userDraftsSnapshot.forEach((doc) => {
            const draft = this._formatDraftData({
              id: doc.id,
              data: () => doc.data()
            });
            drafts.push(draft);
            draftIds.add(doc.id);
          });
          
          console.log('Firebase: Retrieved drafts from user collection', drafts.length);
        } catch (error) {
          console.error('Error getting drafts from user collection:', error);
        }
        
        try {
          // Then, get drafts from main collection that belong to this user
          const draftsRef = collection(db, "drafts");
          const q = query(
            draftsRef,
            where("userId", "==", user.uid),
            orderBy("updatedAt", "desc")
          );
          
          const querySnapshot = await getDocs(q);
          
          querySnapshot.forEach((doc) => {
            // Only add if not already in the list
            if (!draftIds.has(doc.id)) {
              const draft = this._formatDraftData({
                id: doc.id,
                data: () => doc.data()
              });
              drafts.push(draft);
              draftIds.add(doc.id);
            }
          });
          
          console.log('Firebase: Retrieved drafts including main collection', drafts.length);
        } catch (error) {
          console.error('Error getting drafts from main collection:', error);
        }
        
        // Sort by updatedAt timestamp
        return drafts.sort((a, b) => 
          new Date(b.updatedAt) - new Date(a.updatedAt)
        );
      }
    } catch (error) {
      console.error("Error getting drafts:", error);
      throw error;
    }
  }

  // Add an email to the allowed list
  async addAllowedEmail(email) {
    if (!email) {
      throw new Error('Email is required');
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Invalid email format');
    }
    
    if (this.demoMode) {
      console.log('📝 Demo mode: Adding email to allowed list:', email);
      return true;
    }
    
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error('You must be logged in to add allowed emails');
      }
      
      // Check if the current user is an admin
      const isAdmin = await this.isUserAdmin(user.uid);
      if (!isAdmin) {
        throw new Error('Only administrators can add allowed emails');
      }
      
      // Add the email to the allowedEmails collection
      await setDoc(doc(db, 'allowedEmails', email), {
        addedAt: serverTimestamp(),
        addedBy: user.email
      });
      
      console.log('Added email to allowed list:', email);
      return true;
    } catch (error) {
      console.error('Error adding allowed email:', error);
      throw error;
    }
  }
}

const firebaseService = new FirebaseService();
export default firebaseService; 